import "./gallery.css";

import React from "react";

import { GALLERY_PHOTOS, HOME, SECTIONS } from "../../data/content";
import { ChevronIcon } from "../general/Icons";

//Photos shown at first, and added by each "Load More" click
const PAGE_SIZE = 10;

const fullSrc = (photo) => `/images/gallery/${photo.n}.webp`;
const thumbSrc = (photo) => `/images/gallery/thumbs/${photo.n}.webp`;

//Full-screen photo viewer. Arrow keys / buttons move through the photos, Escape closes
const Lightbox = ({ index, onChange, onClose }) => {
  const count = GALLERY_PHOTOS.length;
  const closeRef = React.useRef(null);

  React.useEffect(() => {
    document.body.style.overflow = "hidden";
    const opener = document.activeElement;
    closeRef.current.focus();
    return () => {
      document.body.style.overflow = "";
      if (opener) {
        opener.focus({ preventScroll: true });
      }
    };
  }, []);

  React.useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        onClose();
      }
      else if (e.key === "ArrowRight") {
        onChange((index + 1) % count);
      }
      else if (e.key === "ArrowLeft") {
        onChange((index - 1 + count) % count);
      }
      else if (e.key === "Tab") {
        //Keep focus on the viewer's buttons
        const buttons = [...document.querySelectorAll(".lightbox button")];
        const first = buttons[0];
        const last = buttons[buttons.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
        else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [index, count, onChange, onClose]);

  const photo = GALLERY_PHOTOS[index];
  return (
    <div
      className={"lightbox"}
      role={"dialog"}
      aria-modal={"true"}
      aria-label={"Photo viewer"}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <button
        ref={closeRef}
        type={"button"}
        className={"lightbox-close"}
        aria-label={"Close"}
        onClick={onClose}
      >
        ×
      </button>
      <button
        type={"button"}
        className={"lightbox-arrow is-prev"}
        aria-label={"Previous photo"}
        onClick={() => onChange((index - 1 + count) % count)}
      >
        <ChevronIcon direction={"left"} />
      </button>
      <figure className={"lightbox-figure"}>
        <img
          key={photo.n}
          src={fullSrc(photo)}
          alt={photo.alt}
        />
        <figcaption>
          {index + 1} / {count}
        </figcaption>
      </figure>
      <button
        type={"button"}
        className={"lightbox-arrow is-next"}
        aria-label={"Next photo"}
        onClick={() => onChange((index + 1) % count)}
      >
        <ChevronIcon />
      </button>
    </div>
  );
};

//Edge-to-edge rows of photos (like the Wix collage) - each photo keeps its shape and the rows fill the width
const Gallery = () => {
  const [shown, setShown] = React.useState(PAGE_SIZE);
  const [openIndex, setOpenIndex] = React.useState(null);
  const close = React.useCallback(() => setOpenIndex(null), []);
  const listRef = React.useRef(null);
  const focusFromRef = React.useRef(null);

  //After "Load More", move keyboard focus to the first new photo
  React.useEffect(() => {
    if (focusFromRef.current === null) {
      return;
    }
    const button = listRef.current.querySelectorAll("button")[focusFromRef.current];
    if (button) {
      button.focus({ preventScroll: true });
    }
    focusFromRef.current = null;
  }, [shown]);

  const loadMore = () => {
    focusFromRef.current = shown;
    setShown(Math.min(shown + PAGE_SIZE, GALLERY_PHOTOS.length));
  };

  return (
    <section
      id={SECTIONS.GALLERY}
      className={"gallery"}
    >
      <h2 className={"heading"}>{HOME.GALLERY.HEADING}</h2>

      <ul
        ref={listRef}
        className={"gallery-grid"}
      >
        {GALLERY_PHOTOS.slice(0, shown).map((photo, i) => {
          const ratio = photo.w / photo.h;
          return (
            <li
              key={photo.n}
              className={"gallery-tile"}
              style={{ "--ratio": ratio }}
            >
              <button
                type={"button"}
                onClick={() => setOpenIndex(i)}
                aria-label={`View photo: ${photo.alt}`}
              >
                <span style={{ paddingBottom: `${100 / ratio}%` }} />
                <img
                  src={thumbSrc(photo)}
                  alt={""}
                  loading={"lazy"}
                />
              </button>
            </li>
          );
        })}
        {/* Stops the last row stretching its photos across the whole width */}
        <li
          className={"gallery-filler"}
          aria-hidden={"true"}
        />
      </ul>

      {shown < GALLERY_PHOTOS.length ? (
        <div className={"gallery-more"}>
          <button
            type={"button"}
            className={"btn gallery-more-btn"}
            onClick={loadMore}
          >
            {HOME.GALLERY.LOAD_MORE}
          </button>
        </div>
      ) : null}

      {openIndex !== null ? (
        <Lightbox
          index={openIndex}
          onChange={setOpenIndex}
          onClose={close}
        />
      ) : null}
    </section>
  );
};

export default Gallery;
